import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Space, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import FilterBar, { DEFAULT_PRESET_KEY, resolvePreset } from './FilterBar';
import TeamInsightsTable from './TeamInsightsTable';
import TeamInsightsTimeline from './TeamInsightsTimeline';
import DrillDownDrawer, { DrillDownMetric } from './DrillDownDrawer';
import { fetchTeamInsights } from './teamInsightsService';
import { DailyBreakdown, EventType, TeamInsightsFilters, TeamMemberInsight } from './types';

dayjs.extend(utc);

const yesterdayUtcLabel = (): string =>
    dayjs.utc().subtract(1, 'day').format('YYYY-MM-DD');

const filtersFromSearchParams = (params: URLSearchParams): TeamInsightsFilters => {
    const preset = params.get('preset');
    const from = params.get('from');
    const to = params.get('to');
    // If neither preset nor explicit range, fall back to the default preset.
    if (!preset && !from && !to) return { preset: DEFAULT_PRESET_KEY, from: null, to: null };
    return { preset: preset || null, from: from || null, to: to || null };
};

const filtersToSearchParams = (filters: TeamInsightsFilters): Record<string, string> => {
    const out: Record<string, string> = {};
    if (filters.preset) out.preset = filters.preset;
    else {
        if (filters.from) out.from = filters.from;
        if (filters.to) out.to = filters.to;
    }
    return out;
};

const resolveWindow = (filters: TeamInsightsFilters): { from: string; to: string } | null => {
    const preset = resolvePreset(filters.preset);
    if (preset) {
        const [from, to] = preset.range();
        return { from: from.toISOString(), to: to.toISOString() };
    }
    if (filters.from && filters.to) return { from: filters.from, to: filters.to };
    return null;
};

const TeamInsightsDashboard: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
    const window = useMemo(() => resolveWindow(filters), [filters]);

    const [rows, setRows] = useState<TeamMemberInsight[]>([]);
    const [coderTemplates, setCoderTemplates] = useState<string[]>([]);
    const [daily, setDaily] = useState<DailyBreakdown[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [truncated, setTruncated] = useState<boolean>(false);
    const [drilldown, setDrilldown] = useState<{
        member: TeamMemberInsight;
        metric: DrillDownMetric;
        eventType?: EventType;
    } | null>(null);

    const load = useCallback(async () => {
        if (!window) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchTeamInsights({
                from: window.from,
                to: window.to,
            });
            setRows(response.members);
            setCoderTemplates(response.coderTemplates ?? []);
            setDaily(response.dailyBreakdown);
            setTruncated(response.truncated);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [window]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        document.title = 'Team Insights · Sonat Support';
    }, []);

    const handleFiltersChange = (next: TeamInsightsFilters) => {
        setSearchParams(filtersToSearchParams(next));
    };

    const handleRefresh = () => {
        load();
        message.success('Refreshed');
    };

    return (
        <div style={{ padding: 16 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                    Team insights
                </Typography.Title>
                <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                    Refresh
                </Button>
            </Space>

            <FilterBar value={filters} onChange={handleFiltersChange} />

            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Data through {yesterdayUtcLabel()} (UTC). Today is excluded.
            </Typography.Text>

            {truncated && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Results truncated"
                    description="More than 1000 PRs merged in this window. GitHub's search API caps results at 1000 — pick a shorter window for accurate per-user totals."
                />
            )}

            <TeamInsightsTable
                rows={rows}
                coderTemplates={coderTemplates}
                loading={loading}
                error={error}
                onCellClick={(member, metric, eventType) => setDrilldown({ member, metric, eventType })}
            />

            {!loading && !error && rows.length > 0 && daily.length > 0 && (
                <TeamInsightsTimeline data={daily} members={rows} coderTemplates={coderTemplates} />
            )}

            <DrillDownDrawer
                open={drilldown !== null}
                onClose={() => setDrilldown(null)}
                member={drilldown?.member ?? null}
                metric={drilldown?.metric ?? null}
                eventType={drilldown?.eventType ?? null}
                window={window ?? { from: '', to: '' }}
            />
        </div>
    );
};

export default TeamInsightsDashboard;
